import { environment } from '../../../environments/environment';

// Shape of the IDs SharedService.encodeId() produces. It lives here rather than inside SharedService so
// route matching (RouteAuthService) can recognize an ID segment without importing SharedService -
// that would be a circular import, since SharedService already imports RouteAuthService.

// Legacy marker: prefix + all-digit body, minted before the switch to base36.
export const ID_ENCODING_PREFIX = '8';
// Current marker: prefix + base36 body. A letter can never start a legacy ID, so the two formats
// stay unambiguous and links minted before the switch keep resolving to the same record.
export const ID_ALPHA_ENCODING_PREFIX = 'k';
// base36 (0-9a-z): URL safe, nothing to escape, and case-insensitive on the way back in.
export const ID_ENCODING_RADIX = 36;
export const ID_ENCODED_BODY_PATTERN = /^[0-9a-z]+$/;

// Total length of the encoded string (prefix + body). Kept fixed-width so encoded IDs
// don't leak the magnitude of the original numeric ID via string length.
export function encodedIdLength(): number {
  return environment.id_encoding_length || 15;
}

// Format check only (prefix + charset + fixed length) - does not guarantee the value decodes
// to a real/valid entity, just that it matches the shape one of our encoders produces.
export function isEncodedId(rawId: string | null | undefined): boolean {
  if (!rawId) {
    return false;
  }
  const normalizedId = rawId.toString().toLowerCase();
  if (normalizedId.length !== encodedIdLength()) {
    return false;
  }
  if (normalizedId.startsWith(ID_ALPHA_ENCODING_PREFIX)) {
    return ID_ENCODED_BODY_PATTERN.test(normalizedId);
  }
  return normalizedId.startsWith(ID_ENCODING_PREFIX) && /^\d+$/.test(normalizedId);
}

// A bare, unencoded database ID - a plain number typed straight into the URL or carried over from
// an old link minted before IDs were encoded. Kept separate from "malformed" (a tampered/truncated
// encoded ID) so callers can explain the right thing: the first is no longer allowed at all, the
// second is simply a broken ID.
export function isUnencodedId(rawId: string | null | undefined): boolean {
  if (!rawId) {
    return false;
  }
  const normalizedId = rawId.toString().trim();
  return /^\d+$/.test(normalizedId) && !isEncodedId(normalizedId);
}
